import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Search,
  CheckCircle,
  Clock,
  Shield,
  Star,
  MessageSquare,
  FileText,
  Lock,
  AlertTriangle,
  Info,
  ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { RFQWizard } from './RFQFlow';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Service Icon Component
const ServiceIcon = ({ icon, className = "text-xl" }) => {
  return <span className={className}>{icon}</span>;
};

// ==================== SERVICE CARD VARIANTS ====================

// A) Standard B2B Service Card
export const StandardServiceCard = ({ 
  service, 
  subcategoryName,
  onRequestQuote,
  onMessage,
  currencySymbol = '£',
}) => {
  return (
    <Card className="hover:shadow-lg transition-all duration-200 border-l-4 border-l-blue-500" data-testid={`service-card-${service.key}`}>
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <Badge className="bg-blue-100 text-blue-700 text-xs">
              <Shield className="w-3 h-3 mr-1" />
              Verified Provider
            </Badge>
            <Badge variant="outline" className="text-xs">Business Service</Badge>
          </div>
        </div>

        {/* Service Info */}
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white">
            <ServiceIcon icon={service.icon || "📋"} />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-lg">{service.name}</h3>
            <p className="text-sm text-gray-500">{subcategoryName}</p>
          </div>
        </div>

        {/* Features */}
        <div className="space-y-1.5 mb-4">
          {service.features?.slice(0, 3).map((feature, idx) => (
            <div key={idx} className="flex items-center gap-2 text-sm text-gray-600">
              <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
              <span>{feature}</span>
            </div>
          ))}
        </div>

        {/* Stats & Price */}
        <div className="flex items-center justify-between mb-4 py-2 border-t border-b">
          <div className="flex items-center gap-3 text-sm">
            <span className="flex items-center gap-1">
              <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
              <strong>4.8</strong>
              <span className="text-gray-500">(120 jobs)</span>
            </span>
            <span className="flex items-center gap-1 text-gray-500">
              <Clock className="w-4 h-4" />
              Response &lt; 2h
            </span>
          </div>
          {service.price_from && (
            <span className="font-semibold text-green-600">
              From {currencySymbol}{service.price_from}
            </span>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <Button 
            className="flex-1" 
            onClick={() => onRequestQuote?.(service)}
            data-testid={`request-quote-${service.key}`}
          >
            Request Quote
          </Button>
          <Button 
            variant="outline" 
            onClick={() => onMessage?.(service)}
            data-testid={`message-${service.key}`}
          >
            <MessageSquare className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// B) RFQ / Sourcing Card
export const RFQServiceCard = ({ 
  service, 
  onStartRFQ,
  currencySymbol = '£',
}) => {
  return (
    <Card className="hover:shadow-lg transition-all duration-200 border-l-4 border-l-purple-500 bg-purple-50/30" data-testid={`rfq-card-${service.key}`}>
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex items-center gap-2 mb-3">
          <Badge className="bg-purple-100 text-purple-700 text-xs">
            <FileText className="w-3 h-3 mr-1" />
            Managed Service
          </Badge>
          <Badge variant="outline" className="text-xs">RFQ Support</Badge>
        </div>

        {/* Service Info */}
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center text-white">
            <ServiceIcon icon={service.icon || "📦"} />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-lg">{service.name}</h3>
            <p className="text-sm text-gray-600">{service.tagline || 'Get quotes from verified suppliers'}</p>
          </div>
        </div>

        {/* Features */}
        <div className="space-y-1.5 mb-4">
          {service.rfq_features?.map((feature, idx) => (
            <div key={idx} className="flex items-center gap-2 text-sm text-gray-600">
              <CheckCircle className="w-4 h-4 text-purple-500 flex-shrink-0" />
              <span>{feature}</span>
            </div>
          )) || (
            <>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <CheckCircle className="w-4 h-4 text-purple-500" />
                <span>Get 3+ quotes from verified suppliers</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <CheckCircle className="w-4 h-4 text-purple-500" />
                <span>Bulk pricing support (info-only)</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <CheckCircle className="w-4 h-4 text-purple-500" />
                <span>Delivery options included</span>
              </div>
            </>
          )}
        </div>

        {/* Price & CTA */}
        <div className="flex items-center justify-between">
          {service.rfq_price && (
            <span className="font-semibold text-purple-700">
              From {currencySymbol}{service.rfq_price} / RFQ
            </span>
          )}
          <Button 
            className="bg-purple-600 hover:bg-purple-700"
            onClick={() => onStartRFQ?.(service)}
            data-testid={`start-rfq-${service.key}`}
          >
            Start RFQ
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// C) High-Trust Service Card (Business for Sale / Franchise)
export const HighTrustServiceCard = ({ 
  service, 
  onBookConsultation,
  currencySymbol = '£',
}) => {
  return (
    <Card className="hover:shadow-lg transition-all duration-200 border-l-4 border-l-amber-500 bg-amber-50/30" data-testid={`high-trust-card-${service.key}`}>
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex items-center gap-2 mb-3">
          <Badge className="bg-amber-100 text-amber-700 text-xs">
            <Lock className="w-3 h-3 mr-1" />
            Verified Partner
          </Badge>
          <Badge variant="outline" className="text-xs">High Trust</Badge>
        </div>

        {/* Service Info */}
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center text-white">
            <ServiceIcon icon={service.icon || "🏢"} />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-lg">{service.name}</h3>
            <p className="text-sm text-gray-600">{service.subtitle || 'Professional guidance service'}</p>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="flex items-start gap-2 p-2 bg-blue-50 rounded-lg mb-3 text-sm">
          <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
          <span className="text-blue-700">Not legal or financial advice. For informational purposes only.</span>
        </div>

        {/* Features */}
        <div className="space-y-1.5 mb-4">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Lock className="w-4 h-4 text-amber-500" />
            <span>Confidential handling</span>
          </div>
          {service.trust_features?.map((feature, idx) => (
            <div key={idx} className="flex items-center gap-2 text-sm text-gray-600">
              <CheckCircle className="w-4 h-4 text-amber-500" />
              <span>{feature}</span>
            </div>
          ))}
        </div>

        {/* Price & CTA */}
        <div className="flex items-center justify-between">
          {service.consultation_price && (
            <span className="font-semibold text-amber-700">
              From {currencySymbol}{service.consultation_price}
            </span>
          )}
          <Button 
            className="bg-amber-600 hover:bg-amber-700"
            onClick={() => onBookConsultation?.(service)}
            data-testid={`book-consultation-${service.key}`}
          >
            Book Consultation
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// ==================== SERVICE INQUIRY DIALOG ====================

export const ServiceInquiryDialog = ({ 
  open, 
  onOpenChange, 
  service, 
  subcategorySlug,
  subcategoryName,
  country = 'UK',
}) => {
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    inquiry_details: '',
    budget_range: '',
    timeline: '',
    location: '',
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [inquiryNumber, setInquiryNumber] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/business-services/inquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          subcategory_slug: subcategorySlug,
          service_key: service?.key,
          service_name: service?.name,
          country: country,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setInquiryNumber(data.inquiry_number);
        setSubmitted(true);
        toast.success('Inquiry submitted successfully!');
      } else {
        toast.error('Failed to submit inquiry');
      }
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      toast.error('Failed to submit inquiry');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      customer_name: '',
      customer_email: '',
      customer_phone: '',
      inquiry_details: '',
      budget_range: '',
      timeline: '',
      location: '',
    });
    setSubmitted(false);
    setInquiryNumber('');
  };

  if (!service) return null;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      if (!isOpen) resetForm();
      onOpenChange(isOpen);
    }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ServiceIcon icon={service.icon || "📋"} />
            Request Quote: {service.name}
          </DialogTitle>
        </DialogHeader>

        {submitted ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-semibold text-green-700 mb-2">Inquiry Submitted!</h3>
            <p className="text-gray-600 mb-4">
              Your reference number is:
            </p>
            <div className="bg-gray-100 rounded-lg p-3 font-mono text-lg mb-4">
              {inquiryNumber}
            </div>
            <p className="text-sm text-gray-500">
              We'll match you with verified providers and send quotes within 24-48 hours.
            </p>
            <Button className="mt-4" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Service Info */}
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700">
                <strong>{service.name}</strong> • {subcategoryName}
              </p>
              {service.description && (
                <p className="text-sm text-blue-600 mt-1">{service.description}</p>
              )}
            </div>

            {/* Contact Details */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="customer_name">Your Name *</Label>
                <Input
                  id="customer_name"
                  value={formData.customer_name}
                  onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                  required
                  data-testid="inquiry-name"
                />
              </div>
              <div>
                <Label htmlFor="customer_phone">Phone *</Label>
                <Input
                  id="customer_phone"
                  value={formData.customer_phone}
                  onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                  required
                  data-testid="inquiry-phone"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="customer_email">Email *</Label>
              <Input
                id="customer_email"
                type="email"
                value={formData.customer_email}
                onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                required
                data-testid="inquiry-email"
              />
            </div>

            <div>
              <Label htmlFor="location">Location / Postcode *</Label>
              <Input
                id="location"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="e.g., London SW1A 1AA"
                required
                data-testid="inquiry-location"
              />
            </div>

            {/* Requirements */}
            <div>
              <Label htmlFor="inquiry_details">What do you need? *</Label>
              <Textarea
                id="inquiry_details"
                value={formData.inquiry_details}
                onChange={(e) => setFormData({ ...formData, inquiry_details: e.target.value })}
                placeholder="Describe your requirements in detail..."
                rows={4}
                required
                data-testid="inquiry-details"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="budget_range">Budget Range</Label>
                <Select
                  value={formData.budget_range}
                  onValueChange={(value) => setFormData({ ...formData, budget_range: value })}
                >
                  <SelectTrigger data-testid="inquiry-budget">
                    <SelectValue placeholder="Select budget" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="under_500">Under £500</SelectItem>
                    <SelectItem value="500_1000">£500 - £1,000</SelectItem>
                    <SelectItem value="1000_5000">£1,000 - £5,000</SelectItem>
                    <SelectItem value="5000_10000">£5,000 - £10,000</SelectItem>
                    <SelectItem value="10000_plus">£10,000+</SelectItem>
                    <SelectItem value="best_price">Best Price</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="timeline">Timeline</Label>
                <Select
                  value={formData.timeline}
                  onValueChange={(value) => setFormData({ ...formData, timeline: value })}
                >
                  <SelectTrigger data-testid="inquiry-timeline">
                    <SelectValue placeholder="When needed?" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="urgent">Urgent (within 48h)</SelectItem>
                    <SelectItem value="this_week">This week</SelectItem>
                    <SelectItem value="next_week">Next week</SelectItem>
                    <SelectItem value="this_month">This month</SelectItem>
                    <SelectItem value="flexible">Flexible</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button type="submit" className="w-full" disabled={loading} data-testid="submit-inquiry">
              {loading ? 'Submitting...' : 'Submit Inquiry'}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ==================== MAIN SERVICES DISPLAY COMPONENT ====================

export default function BusinessServicesDisplay({ 
  subcategorySlug, 
  country = 'UK',
}) {
  const [services, setServices] = useState({});
  const [subcategoryInfo, setSubcategoryInfo] = useState(null);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [selectedService, setSelectedService] = useState(null);
  const [showInquiryDialog, setShowInquiryDialog] = useState(false);
  const [showRFQWizard, setShowRFQWizard] = useState(false);

  const currencySymbol = country === 'UK' ? '£' : 'Rs.';

  // High-trust subcategories that need special treatment
  const highTrustSubcategories = ['business-for-sale', 'franchises'];
  const rfqSubcategories = ['manufacturing-supplies', 'wholesale-bulk-goods'];

  useEffect(() => {
    const fetchServices = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${API_URL}/api/business-services/config/subcategory/${subcategorySlug}?country=${country}`
        );
        const data = await response.json();
        
        if (data.success) {
          setSubcategoryInfo(data.subcategory);
          setServices(data.subcategory?.services || {});
        }
      } catch (error) {
        console.error('Failed to fetch services:', error);
      } finally {
        setLoading(false);
      }
    };

    const fetchProviders = async () => {
      try {
        const response = await fetch(
          `${API_URL}/api/business-services/providers?subcategory=${subcategorySlug}&country=${country}&verified_only=true&limit=4`
        );
        const data = await response.json();
        if (data.success) {
          setProviders(data.providers || []);
        }
      } catch (error) {
        console.error('Failed to fetch providers:', error);
      }
    };

    if (subcategorySlug) {
      fetchServices();
      fetchProviders();
    }
  }, [subcategorySlug, country]);

  // Filter services
  const filteredServices = Object.entries(services).filter(([key, service]) => {
    const matchesSearch = !searchTerm || 
      service.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      service.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterType === 'all' || service.service_type === filterType;
    
    return matchesSearch && matchesFilter;
  });

  // Handle service actions
  const handleRequestQuote = (service) => {
    setSelectedService({ ...service, key: Object.keys(services).find(k => services[k] === service) });
    setShowInquiryDialog(true);
  };

  const handleStartRFQ = (service) => {
    setSelectedService({ ...service, key: Object.keys(services).find(k => services[k] === service) });
    setShowRFQWizard(true);
  };

  const handleBookConsultation = (service) => {
    setSelectedService({ ...service, key: Object.keys(services).find(k => services[k] === service) });
    setShowInquiryDialog(true);
  };

  // Get card type for service
  const getCardType = (serviceKey, service) => {
    if (highTrustSubcategories.includes(subcategorySlug)) {
      return 'high-trust';
    }
    if (rfqSubcategories.includes(subcategorySlug) || service.is_rfq_service) {
      return 'rfq';
    }
    return 'standard';
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-10 w-64 bg-gray-200 animate-pulse rounded"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-48 bg-gray-100 animate-pulse rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (!subcategoryInfo || Object.keys(services).length === 0) {
    return null;
  }

  return (
    <div className="py-4" data-testid="business-services-display">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <ServiceIcon icon={subcategoryInfo.subcategory_icon} className="text-2xl" />
            Available Services
          </h2>
          <p className="text-sm text-gray-500">
            {Object.keys(services).length} services in {subcategoryInfo.subcategory_name}
          </p>
        </div>
      </div>

      {/* Compliance Warning */}
      {subcategoryInfo.compliance_level === 'high' && (
        <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4">
          <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <strong className="text-amber-700">High-Trust Category</strong>
            <p className="text-amber-600">{subcategoryInfo.disclaimer || 'Professional guidance only. Not legal or financial advice.'}</p>
          </div>
        </div>
      )}

      {/* Featured Providers Section */}
      {providers.length > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-500" />
            Verified Providers
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {providers.map((provider) => (
              <Card key={provider.id} className="hover:shadow-md transition-shadow" data-testid={`provider-card-${provider.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-3">
                    {provider.logo_url ? (
                      <img 
                        src={provider.logo_url} 
                        alt={provider.name}
                        className="w-12 h-12 rounded-lg object-cover"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold">
                        {provider.name?.charAt(0) || 'B'}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-sm truncate">{provider.name}</h4>
                      <p className="text-xs text-gray-500 truncate">{provider.service_area}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-1">
                      <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      <strong>{provider.rating || 4.5}</strong>
                      <span className="text-gray-500">({provider.review_count || 0})</span>
                    </span>
                    {provider.verified && (
                      <Badge className="bg-green-100 text-green-700 text-xs">Verified</Badge>
                    )}
                  </div>
                  {provider.certifications?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {provider.certifications.slice(0, 2).map((cert, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">{cert}</Badge>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search services..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="service-search"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {['all', 'service', 'supply', 'consultation', 'transaction'].map((type) => (
            <Button
              key={type}
              variant={filterType === type ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterType(type)}
              className="capitalize"
              data-testid={`filter-${type}`}
            >
              {type === 'all' ? 'All' : type}
            </Button>
          ))}
        </div>
      </div>

      {/* Services Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filteredServices.map(([key, service]) => {
          const cardType = getCardType(key, service);
          
          if (cardType === 'high-trust') {
            return (
              <HighTrustServiceCard
                key={key}
                service={{ ...service, key }}
                onBookConsultation={handleBookConsultation}
                currencySymbol={currencySymbol}
              />
            );
          }
          
          if (cardType === 'rfq') {
            return (
              <RFQServiceCard
                key={key}
                service={{ ...service, key }}
                onStartRFQ={handleStartRFQ}
                currencySymbol={currencySymbol}
              />
            );
          }
          
          return (
            <StandardServiceCard
              key={key}
              service={{ ...service, key }}
              subcategoryName={subcategoryInfo.subcategory_name}
              onRequestQuote={handleRequestQuote}
              onMessage={() => toast.info('Message feature coming soon!')}
              currencySymbol={currencySymbol}
            />
          );
        })}
      </div>

      {filteredServices.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <Search className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>No services found matching your criteria</p>
        </div>
      )}

      {/* Inquiry Dialog */}
      <ServiceInquiryDialog
        open={showInquiryDialog}
        onOpenChange={setShowInquiryDialog}
        service={selectedService}
        subcategorySlug={subcategorySlug}
        subcategoryName={subcategoryInfo?.subcategory_name}
        country={country}
      />

      {/* RFQ Wizard Dialog */}
      <RFQWizard
        open={showRFQWizard}
        onOpenChange={setShowRFQWizard}
        subcategory={subcategoryInfo}
        country={country}
        onComplete={(data) => {
          toast.success(`RFQ ${data.rfq_number} submitted successfully!`);
          setShowRFQWizard(false);
        }}
      />
    </div>
  );
}
